import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ValidationErrors } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, filter, map, take, timer } from 'rxjs';
import {
  formatRelativeTimestamp,
  formatTimestamp,
} from 'src/utils/general-utils';
import { AlertService } from '../../services/alert.service';
import {
  ImpersonationHistoryEntry,
  ImpersonationService,
} from '../../services/impersonation.service';
import { mayStartImpersonation } from '../../models/user.model';
import { selectProfileData } from '../../stores/profile/profile.selector';
import { ProfileState } from '../../stores/store.states';

export interface TimeLeft {
  expired: boolean;
  formatted: string;
}

/** {@link Validators.required} accepts a string of spaces, which the backend then rejects. */
function notBlank(control: AbstractControl): ValidationErrors | null {
  return `${control.value ?? ''}`.trim() ? null : { required: true };
}

/**
 * The one place in the front-end that knows about impersonation: a masteradmin or a support user
 * starts one here and stops it here, and every other page simply shows whatever the impersonated
 * user would see.
 */
@Component({
  selector: 'app-impersonation',
  templateUrl: './impersonation.component.html',
  styleUrls: ['./impersonation.component.scss'],
  standalone: false,
})
export class ImpersonationComponent implements OnInit {
  /** How many impersonations one "load more" adds. */
  private static readonly HISTORY_PAGE_SIZE = 10;

  profile$ = this.store.select(selectProfileData);

  /** Ticks every second, so the remaining time counts down while the tab stays open. */
  timeLeft$ = combineLatest([this.profile$, timer(0, 1000)]).pipe(
    map(([profile]) => this.timeLeft(profile.impersonationExpiresAt)),
  );

  form = this.formBuilder.nonNullable.group({
    impersonatedUser: this.formBuilder.control('', {
      nonNullable: true,
      validators: [notBlank],
    }),
    justification: this.formBuilder.control('', {
      nonNullable: true,
      validators: [notBlank],
    }),
  });

  pending = false;

  history: ImpersonationHistoryEntry[] = [];
  historyPending = false;
  /** Set while the history has a further page, and passed back to ask for it. */
  private nextBefore?: string;

  constructor(
    private store: Store,
    private formBuilder: FormBuilder,
    private impersonationService: ImpersonationService,
    private alertService: AlertService,
    private translate: TranslateService,
    private destroyRef: DestroyRef,
  ) {}

  protected readonly mayStartImpersonation = mayStartImpersonation;

  ngOnInit() {
    // The history is privileged, so it waits for a profile that says it may be read rather than
    // for one that is merely loaded: on a reload the store reaches loadingState SUCCESS once for
    // the token profile, before the one from the backend that carries the privileged role.
    this.profile$
      .pipe(
        filter((profile) => this.mayReadHistory(profile)),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.loadMoreHistory());
  }

  private mayReadHistory(profile: ProfileState): boolean {
    return mayStartImpersonation(profile) || !!profile.impersonatedBy;
  }

  get hasMoreHistory(): boolean {
    return !!this.nextBefore;
  }

  /**
   * Loads the next page. Paged on the time of the last entry rather than on an offset, so an
   * impersonation started while the list is open cannot shift the page boundaries.
   */
  loadMoreHistory() {
    if (this.historyPending) {
      return;
    }
    this.historyPending = true;
    this.impersonationService
      .history(ImpersonationComponent.HISTORY_PAGE_SIZE, this.nextBefore)
      .subscribe({
        next: (page) => {
          this.history = [...this.history, ...(page.impersonations ?? [])];
          this.nextBefore = page.nextBefore;
          this.historyPending = false;
        },
        error: () => {
          this.historyPending = false;
          this.alertService.error(
            this.translate.instant('impersonation.error.history'),
          );
        },
      });
  }

  formatTime(timestamp?: string): string {
    return formatTimestamp(timestamp);
  }

  formatTimeAgo(timestamp?: string): string {
    const locale =
      this.translate.currentLang || this.translate.getDefaultLang() || 'en';
    return formatRelativeTimestamp(timestamp, locale);
  }

  start() {
    if (this.form.invalid || this.pending) {
      return;
    }
    const { impersonatedUser, justification } = this.form.getRawValue();
    this.pending = true;
    this.impersonationService
      .start(impersonatedUser.trim(), justification.trim())
      .subscribe({
        next: () => this.reloadApplication(),
        error: (error) => this.failed(error),
      });
  }

  stop() {
    if (this.pending) {
      return;
    }
    this.pending = true;
    this.impersonationService.stop().subscribe({
      next: () => this.reloadApplication(),
      error: (error) => this.failed(error),
    });
  }

  /** A user as one line, for the running impersonation as well as for the history. */
  userLabel(user?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  }): string {
    const name = [user?.firstName, user?.lastName]
      .filter((part) => !!part?.trim())
      .join(' ');
    const email = user?.email?.trim();

    return [name, email && name ? `(${email})` : email]
      .filter((part) => !!part)
      .join(' ');
  }

  /**
   * Impersonation changes what every store holds, so switching it reloads the application rather
   * than trying to refresh each one.
   */
  protected reloadApplication() {
    window.location.reload();
  }

  private failed(error: unknown) {
    this.pending = false;
    this.alertService.error(this.errorMessage(error));
  }

  private errorMessage(error: unknown): string {
    const status = (error as HttpErrorResponse)?.status;
    if (status === 404) {
      return this.translate.instant('impersonation.error.user_not_found');
    }
    if (status === 403) {
      return this.translate.instant('impersonation.error.forbidden');
    }
    return this.translate.instant('impersonation.error.failed');
  }

  private timeLeft(expiresAt?: string): TimeLeft {
    const remainingMs = expiresAt
      ? new Date(expiresAt).getTime() - Date.now()
      : 0;
    if (remainingMs <= 0) {
      return { expired: true, formatted: '0:00' };
    }

    const totalSeconds = Math.floor(remainingMs / 1000);
    const seconds = `${totalSeconds % 60}`.padStart(2, '0');
    return {
      expired: false,
      formatted: `${Math.floor(totalSeconds / 60)}:${seconds}`,
    };
  }
}
